/**
 * Player survey response types and Firestore helpers.
 *
 * - users/{uid}.playerSurveyCompleted — gate flag (client may set true once)
 * - users/{uid}/surveyResponses/player-v1 — completed payload (create-once for players;
 *   admin may update own doc when retaking via admin console toggle)
 *
 * Draft answers live in localStorage only.
 * Client `validatePlayerSurveyAnswers` sanitizes known keys then accepts blanks;
 * create-once + top-level shape (`surveyId`, `schemaVersion`, `environment`, `answers`)
 * are enforced in firestore.rules (`validSurveyResponseWrite`). Answer-key detail is
 * intentionally client-side (see rules comment on surveyResponses).
 * Ground truth copy: docs/player-survey.md
 */
import {
  doc,
  getDoc,
  type Firestore,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import {
  PLAYER_SURVEY_ANSWER_KEYS,
  PLAYER_SURVEY_CHECKBOX_KEYS,
  PLAYER_SURVEY_ID,
  PLAYER_SURVEY_PAGE_COUNT,
  PLAYER_SURVEY_Q8A_MAX_LEN,
  PLAYER_SURVEY_Q24_MAX_LEN,
  PLAYER_SURVEY_QUESTION_BY_ID,
  PLAYER_SURVEY_SCHEMA_VERSION,
  type PlayerSurveyAnswerKey,
  type SurveyAnswerValue,
} from '../constants/playerSurvey';

export const PLAYER_SURVEY_DRAFT_STORAGE_PREFIX = 'tt-survey-player-v1:';
export const PLAYER_SURVEY_ADMIN_RETAKE_STORAGE_PREFIX = 'tt-survey-admin-retake:';

export type PlayerSurveyEnvironment = 'development' | 'production';

/** Dev (`tenteams-dev`) and prod (`tenteams`) are separate Firestore projects; this tags the payload too. */
export function playerSurveyEnvironment(): PlayerSurveyEnvironment {
  return import.meta.env.VITE_FIREBASE_ENV === 'development' ? 'development' : 'production';
}

export type PlayerSurveyAnswers = Partial<Record<PlayerSurveyAnswerKey, SurveyAnswerValue>>;

export interface PlayerSurveyDraft {
  answers: PlayerSurveyAnswers;
  pageIndex: number;
}

export interface PlayerSurveyResponseDoc {
  surveyId: typeof PLAYER_SURVEY_ID;
  schemaVersion: typeof PLAYER_SURVEY_SCHEMA_VERSION;
  completedAt: Timestamp;
  answers: PlayerSurveyAnswers;
  environment: PlayerSurveyEnvironment;
}

export function playerSurveyDraftStorageKey(uid: string): string {
  return `${PLAYER_SURVEY_DRAFT_STORAGE_PREFIX}${uid}`;
}

export function adminSurveyRetakeStorageKey(uid: string): string {
  return `${PLAYER_SURVEY_ADMIN_RETAKE_STORAGE_PREFIX}${uid}`;
}

/** Admin-only local flag: show survey on /profile even after completion (for retake). */
export function getAdminSurveyRetakeEnabled(uid: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(adminSurveyRetakeStorageKey(uid)) === '1';
  } catch {
    return false;
  }
}

export function setAdminSurveyRetakeEnabled(uid: string, enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const key = adminSurveyRetakeStorageKey(uid);
    if (enabled) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function loadPlayerSurveyDraft(uid: string): PlayerSurveyDraft | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(playerSurveyDraftStorageKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PlayerSurveyDraft>;
    if (!parsed || typeof parsed !== 'object') return null;
    const pageIndex =
      typeof parsed.pageIndex === 'number' &&
      parsed.pageIndex >= 1 &&
      parsed.pageIndex <= PLAYER_SURVEY_PAGE_COUNT
        ? parsed.pageIndex
        : 1;
    const answers = sanitizePlayerSurveyAnswers(
      parsed.answers && typeof parsed.answers === 'object' && !Array.isArray(parsed.answers)
        ? (parsed.answers as PlayerSurveyAnswers)
        : {},
    );
    return { answers, pageIndex };
  } catch {
    return null;
  }
}

export function savePlayerSurveyDraft(uid: string, draft: PlayerSurveyDraft): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(playerSurveyDraftStorageKey(uid), JSON.stringify(draft));
  } catch {
    // Quota / private mode — ignore
  }
}

export function clearPlayerSurveyDraft(uid: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(playerSurveyDraftStorageKey(uid));
  } catch {
    // ignore
  }
}

/** Drop unanswered / unknown / stale option values; never blocks submit. */
export function sanitizePlayerSurveyAnswers(answers: PlayerSurveyAnswers): PlayerSurveyAnswers {
  const out: PlayerSurveyAnswers = {};
  for (const key of PLAYER_SURVEY_ANSWER_KEYS) {
    const value = answers[key];
    if (value === undefined || value === null) continue;
    const def = PLAYER_SURVEY_QUESTION_BY_ID[key];

    if (key === 'q15') {
      if (typeof value === 'number' && Number.isFinite(value)) {
        const n = Math.round(value);
        if (n >= 0 && n <= 10) out.q15 = n;
      }
      continue;
    }

    if (PLAYER_SURVEY_CHECKBOX_KEYS.includes(key as (typeof PLAYER_SURVEY_CHECKBOX_KEYS)[number])) {
      if (!Array.isArray(value)) continue;
      const allowed = new Set(def.options ?? []);
      const filtered = value.filter((v): v is string => typeof v === 'string' && allowed.has(v));
      if (filtered.length > 0) out[key] = filtered;
      continue;
    }

    if (def.kind === 'shortText' || def.kind === 'longText') {
      if (typeof value !== 'string') continue;
      const trimmed = value.trim();
      if (!trimmed) continue;
      const max = key === 'q24' ? PLAYER_SURVEY_Q24_MAX_LEN : PLAYER_SURVEY_Q8A_MAX_LEN;
      out[key] = trimmed.slice(0, max);
      continue;
    }

    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (def.options && !def.options.includes(trimmed)) continue;
    out[key] = trimmed;
  }

  if (out.q8 !== 'Other') {
    delete out.q8a;
  }
  return out;
}

/** @deprecated Use sanitizePlayerSurveyAnswers — kept as alias for older call sites/tests. */
export function normalizePlayerSurveyAnswers(answers: PlayerSurveyAnswers): PlayerSurveyAnswers {
  return sanitizePlayerSurveyAnswers(answers);
}

export type PlayerSurveyValidationResult =
  | { ok: true; answers: PlayerSurveyAnswers }
  | { ok: false; message: string };

/**
 * Prepare answers for submit. Always succeeds: invalid/stale options are dropped,
 * unanswered questions stay omitted. Submit must never fail on answer shape.
 */
export function validatePlayerSurveyAnswers(raw: PlayerSurveyAnswers): PlayerSurveyValidationResult {
  return { ok: true, answers: sanitizePlayerSurveyAnswers(raw) };
}

export function isSurveyQuestionVisible(
  question: { whenKey?: PlayerSurveyAnswerKey; whenValue?: string },
  answers: PlayerSurveyAnswers,
): boolean {
  if (!question.whenKey) return true;
  return answers[question.whenKey] === question.whenValue;
}

export function surveyResponseDocRef(db: Firestore, uid: string) {
  return doc(db, 'users', uid, 'surveyResponses', PLAYER_SURVEY_ID);
}

/** Load saved answers for admin retake (owner or admin read). */
export async function loadPlayerSurveyResponseAnswers(
  db: Firestore,
  uid: string,
): Promise<PlayerSurveyAnswers | null> {
  const snap = await getDoc(surveyResponseDocRef(db, uid));
  if (!snap.exists()) return null;
  const data = snap.data() as Partial<PlayerSurveyResponseDoc>;
  if (!data.answers || typeof data.answers !== 'object') return {};
  return sanitizePlayerSurveyAnswers(data.answers);
}

/**
 * Create-once survey response + set playerSurveyCompleted.
 * When `retake` is true (admin only; rules enforce admin claim), overwrite the existing
 * response doc and leave playerSurveyCompleted as-is.
 */
export async function submitPlayerSurvey(
  db: Firestore,
  uid: string,
  rawAnswers: PlayerSurveyAnswers,
  options?: { retake?: boolean },
): Promise<void> {
  const answers = sanitizePlayerSurveyAnswers(rawAnswers);
  const payload = {
    surveyId: PLAYER_SURVEY_ID,
    schemaVersion: PLAYER_SURVEY_SCHEMA_VERSION,
    completedAt: Timestamp.now(),
    answers,
    environment: playerSurveyEnvironment(),
  } satisfies PlayerSurveyResponseDoc;

  const batch = writeBatch(db);
  batch.set(surveyResponseDocRef(db, uid), payload);
  if (!options?.retake) {
    batch.update(doc(db, 'users', uid), { playerSurveyCompleted: true });
  }
  await batch.commit();
  clearPlayerSurveyDraft(uid);
}
